import moment from "moment";
import dayjs, { Dayjs } from "dayjs";
import { RuleObject } from "antd/es/form";
import { StoreValue } from "antd/es/form/interface";
import { Rule } from "antd/es/form";
import { FormInstance } from "antd";
import Text from "../context/language-context";

const currentLang =
  (localStorage.getItem("rcml-lang") as "en" | "hi" | "sk") || "en";

/**
 * Utility for input field validation
 */

/**
 * Validates that the input is not empty or whitespace.
 * @param fieldName - The name of the field for the error message.
 * @returns A validation function for Ant Design rules.
 */
export const requiredFieldValidator = (fieldName: string) => {
  return (_: unknown, value: string) => {
    if (!value || value.trim() === "") {
      return Promise.reject(new Error(`Please ${fieldName}`));
    }
    return Promise.resolve();
  };
};

/**
 * Allow only numeric input in an input field.
 * Blocks alphabetic and special characters, but allows:
 * - Digits (0-9)
 * - Backspace, Tab, Arrow keys, Delete, Home, End
 * - Ctrl/Cmd + A, C, V, X (for accessibility and convenience)
 *
 * @param {React.KeyboardEvent<HTMLInputElement>} e - The keyboard event from input field
 */

export const allowOnlyNumericInput_onKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Delete",
    "Home",
    "End",
  ];

  // Allow Ctrl/Cmd + A, C, V, X
  if (
    (e.ctrlKey || e.metaKey) &&
    ["a", "c", "v", "x"].includes(e.key.toLowerCase())
  ) {
    return;
  }

  if (!/^\d$/.test(e.key) && !allowedKeys.includes(e.key)) {
    e.preventDefault();
  }
};

/**
 * Validates that the age input is a number within the allowed range.
 * @param fieldName - The name of the field for the error message.
 * @param min - Minimum allowed age (default is 1).
 * @param max - Maximum allowed age (default is 120).
 * @returns A validation function for Ant Design rules.
 */
export const ageFieldValidator_rules = (
  fieldName: string,
  min = 1,
  max = 120
) => {
  return (_: unknown, value: string) => {
    if (!value || value.trim() === "") {
      return Promise.reject(new Error(`Please enter ${fieldName}`));
    }

    const num = Number(value);

    if (isNaN(num)) {
      return Promise.reject(new Error(`${fieldName} must be a number`));
    }

    if (num < min || num > max) {
      return Promise.reject(
        new Error(`${fieldName} must be between ${min} and ${max}`)
      );
    }

    return Promise.resolve();
  };
};

/**
 * Disables dates in the future for Ant Design DatePicker.
 * @param current - The current date being rendered.
 * @returns true if the date is after today (disabled), false otherwise.
 */
export const disableFutureDates_DatePicker_disableDate = (current: Dayjs) => {
  // Disable dates after today
  return current && current.isAfter(dayjs(), "day");
};

/**
 * Returns today's date in YYYY-MM-DD format for input[type="date"] max attribute
 */
export const disableFutureDates_on_input_date_max = (): string => {
  return new Date().toISOString().split("T")[0];
};

/**
 * Returns current year and month in YYYY-MM format for input[type="month"] max attribute
 */
export const disableFutureDates_on_input_month_max = (): string => {
  return new Date().toISOString().slice(0, 7);
};

/**
 * Generates validation rules for number input fields in Ant Design forms.
 *
 * @param fieldName - Label of the field to be shown in error messages.
 * @param options - Optional settings.
 * @param options.required - Whether the field is required. Default is true.
 * @param options.allowNegative - Whether to allow negative numbers. Default is false.
 * @param options.min - Minimum allowed value (inclusive).
 * @param options.max - Maximum allowed value (inclusive).
 * @returns An array of Ant Design rule objects for number validation.
 */
export const numberInputRules = ({
  required = true,
  allowNegative = false,
  min,
  max,
}: {
  required?: boolean;
  allowNegative?: boolean;
  min?: number;
  max?: number;
} = {}): Rule[] => {
  const validationMessage = {
    VALID_NUMBER: {
      en: "Please enter a valid number",
      hi: "कृपया एक मान्य संख्या दर्ज करें",
      sk: "कृपया एकं मान्यसंख्यां प्रविश्यताम्",
    },
    NON_NEGATIVE_NUMBER: {
      en: "The number must not be negative",
      hi: "संख्या ऋणात्मक नहीं होनी चाहिए",
      sk: "संख्या ऋणात्मिका न भवेत्",
    },
    MIN_VALUE: {
      en: "The minimum allowed value is",
      hi: "न्यूनतम मान यह होना चाहिए",
      sk: "न्यूनतमः अनुमतमानः अस्ति",
    },
    MAX_VALUE: {
      en: "The maximum allowed value is",
      hi: "अधिकतम मान यह हो सकता है",
      sk: "अधिकतमः अनुमतमानः अस्ति",
    },
    REQUIRED: {
      en: "This field is required",
      hi: "यह सूचना अनिवार्य है!",
      sk: "एषा सूचना अनिवार्या अस्ति।",
    },
  };

  const getMessage = (
    key: keyof typeof validationMessage,
    suffix?: string | number
  ) => {
    return `${validationMessage[key][currentLang]}${suffix !== undefined ? ` ${suffix}` : ""}`;
  };

  return [
    ...(required
      ? [
          {
            required: true,
            message: getMessage("REQUIRED"),
          },
        ]
      : []),
    {
      validator: (_, value) => {
        if (value === undefined || value === "") {
          return Promise.resolve(); // required handles this
        }

        const numberValue = Number(value);

        if (isNaN(numberValue)) {
          return Promise.reject(getMessage("VALID_NUMBER"));
        }

        if (!allowNegative && numberValue < 0) {
          return Promise.reject(getMessage("NON_NEGATIVE_NUMBER"));
        }

        if (min !== undefined && numberValue < min) {
          return Promise.reject(getMessage("MIN_VALUE", min));
        }

        if (max !== undefined && numberValue > max) {
          return Promise.reject(getMessage("MAX_VALUE", max));
        }

        return Promise.resolve();
      },
    },
  ];
};
/**
 * Validates a file for type and size restrictions.
 *
 * @param file - The file object to validate.
 * @returns An object with `valid` as a boolean indicating if the file passed validation,
 *          and `error` as a string containing the error message if validation fails.
 *
 * The validation checks:
 * - Only allows files with MIME types: image/png, image/jpeg, application/pdf.
 * - Restricts file size to a maximum of 5MB.
 *
 * Usage:
 * ```ts
 * const result = validateFile(file);
 * if (!result.valid) {
 *   console.error(result.error);
 * }
 * ```
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  const allowedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "application/pdf",
  ];
  const maxSize = 3 * 1024 * 1024; // 3MB

  const lang =
    (localStorage.getItem("rcml-lang") as "en" | "hi" | "sk") || "en";

  const validationMessage = {
    FILE_TYPE_ERROR: {
      en: "Only PNG, JPEG, and PDF files are allowed!",
      hi: "केवल PNG, JPEG और PDF फ़ाइलें ही अनुमत हैं!",
      sk: "केवलं PNG, JPEG च PDF सञ्चिकाः एव अनुमत्या: सन्ति!",
    },
    FILE_SIZE_ERROR: {
      en: "File size should not be greater than 3MB!",
      hi: "फ़ाइल का आकार 3MB से अधिक नहीं होना चाहिए!",
      sk: "सञ्चिकायाः आकारः 3MB तः अधिकः न भवेत्!",
    },
  };

  if (file.size > maxSize) {
    return {
      valid: false,
      error: validationMessage.FILE_SIZE_ERROR[lang],
    };
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: validationMessage.FILE_TYPE_ERROR[lang],
    };
  }

  return { valid: true };
}
